import { getConfigManager } from '../../config';
import { MULTICALL_CONTRACT_ADDRESS } from '../../constants';
import { Decimal, DEFAULT_DECIMAL_PRECISION } from '../../datastructures';
import { InterestRateConfig, NostraToken } from '../../interfaces';
import { MultiCallContract } from '../../services';
import nostraTokenListAtom from './nostraTokenListAtom';
import { createAsyncAtom } from '../../lib/state';
import { RpcProvider } from 'starknet';
import rpcProviderAtom from '../rpcProviderAtom';

export type InterestRateConfigMap = { [debtTokenAddress: string]: InterestRateConfig };

const RESPONSE_CHUNK_SIZE = 14;

const fetchData = async (debtTokens: NostraToken[], provider: RpcProvider) => {
  try {
    const config = getConfigManager().getConfig();

    const calldata = debtTokens.map(debtToken => {
      return {
        contractAddress: config.interestRateModel,
        entrypoint: 'getInterestRateConfig',
        calldata: [debtToken.address],
      };
    });

    const multiCallContract = new MultiCallContract(MULTICALL_CONTRACT_ADDRESS, provider);

    const result = await multiCallContract.aggregate(calldata);

    if (!result) {
      return null;
    }

    const interestRateConfigMap: InterestRateConfigMap = {};

    for (let i = 0; i < result.length; i += RESPONSE_CHUNK_SIZE) {
      // Index 1,2 is optimalUtilizationRate
      // Index 3,4 is baseBorrowRate
      // Index 5,6 is rateSlope1
      // Index 7,8 is rateSlope2
      // Index 9,10 is generalProtocolFee
      const optimalUtilizationRate = new Decimal(
        { low: result[i + 1], high: result[i + 2] },
        DEFAULT_DECIMAL_PRECISION,
      );
      const baseBorrowRate = new Decimal({ low: result[i + 3], high: result[i + 4] }, DEFAULT_DECIMAL_PRECISION);
      const rateSlope1 = new Decimal({ low: result[i + 5], high: result[i + 6] }, DEFAULT_DECIMAL_PRECISION);
      const rateSlope2 = new Decimal({ low: result[i + 7], high: result[i + 8] }, DEFAULT_DECIMAL_PRECISION);
      const generalProtocolFee = new Decimal({ low: result[i + 9], high: result[i + 10] }, DEFAULT_DECIMAL_PRECISION);

      interestRateConfigMap[debtTokens[i / RESPONSE_CHUNK_SIZE].address] = {
        optimalUtilizationRate,
        baseBorrowRate,
        rateSlope1,
        rateSlope2,
        generalProtocolFee,
      };
    }

    return interestRateConfigMap;
  } catch (error) {
    console.error(`useInterestRateConfig - Failed to load interest rate config!`, error);
    return null;
  }
};

const interestRateConfigAtom = createAsyncAtom(async get => {
  const nostraTokenList = await get(nostraTokenListAtom);
  const provider = await get(rpcProviderAtom);

  const debtTokens = nostraTokenList.filter(nostraToken => nostraToken.variant === 'debt');

  return fetchData(debtTokens, provider);
});

export default interestRateConfigAtom;
